import _get from 'lodash/get'
import axios from 'axios'
import GetCookie from '../utils/getcookie'

const bowser = require('bowser')

var FontFaceObserver = require('fontfaceobserver')

/**
 * Fixed Preview Class
 *
 *  NOTE: SVG class should already exist on the page when this code is run
 *
 */
export default class {

  constructor (customband) {
    // this.debug = process.env.NODE_ENV !== 'production'
    this.debug = false
    this.draw = null
    this.customband = customband
    this.debossedEffect = null
    this.debossedEffectNoColor = null
    this.embossedEffect = null
    this.edgeBlurEffect = null
    this.defaultGraphicWidth = 35
    this.sliverMaskWidth = 3
    this.addingToCart = false
    this.isRendered = false
    this.graphicPaddingXAmt = 10
    this.browser = bowser.getParser(window.navigator.userAgent);

    let getCookie = new GetCookie()
    this.axiosConfig = {
      headers: {
        'X-CSRFToken': getCookie.csrf()
      }
    }
  }

  setSVG (draw) {
    this.draw = draw
    this.draw.viewbox(0, 0, 950, 250)
    this.render()
  }

  render () {
    this.isRendered = false
    this.draw.clear()
    this.addFilters()
    this.renderProductColor()
    this.renderAreas()
  }

  renderProduct () {
    this.renderProductColor()
    if (!this.customband.isBlank()) {
      this.renderAreas()
    }
  }

  addFilters () {
    /**
    For a good explanation of this inner shadow effect See:
      https://www.xanthir.com/b4Yv0
    */

    /**
     * DEBOSSED EFFECT - With color
     */
    /* eslint-disable no-undef */
    this.debossedEffect = this.createDebossedFilter(2, true)
    this.debossedEffectSmall = this.createDebossedFilter(1, true)

    /**
     * DEBOSSED EFFECT - With no color
     */
    /* eslint-disable no-undef */
    this.debossedEffectNoColor = this.createDebossedFilter(2, false)
    this.debossedEffectNoColorSmall = this.createDebossedFilter(1, false)

    /**
     * EMBOSSED EFFECT
     */
    /* eslint-disable no-undef */
    this.embossedEffect = this.createEmbossedFilter(true)

    /**
     * Blur the edges of the color containers (ex. marbleized paths)
     */
    this.edgeBlurEffect = new SVG.Filter()
    this.edgeBlurEffect.gaussianBlur(6).in(this.edgeBlurEffect.source)
  }

  createDebossedFilter (blur = 2, hasColor = false) {
    let filter = new SVG.Filter() // Already imported into the page (See "components/BandPreview"
    filter.componentTransfer({
      a: {type: 'table', tableValues: [1, 0]}
    })
    filter.gaussianBlur(blur).in(null)
    filter.offset(1, 1).result('offsetblur')
    filter.flood('rgb(20, 0, 0)').result('color')
    filter.composite(null, 'offsetblur', 'in')
    filter.composite(null, filter.sourceAlpha, 'in').result('shadow')
    filter.componentTransfer({
      a: {type: 'linear', slope: 0.15}
    }).in(filter.source).result('nocolor')
    if (hasColor) {
      filter.merge(filter.source, 'shadow')
    } else {
      filter.merge('nocolor', 'shadow')
    }

    return filter
  }

  createEmbossedFilter (hasColor = false) {
    return this.createEmbossedFilter2()
  }

  /**
   * This filter comes from RaphaelJS's emboss() function
   *
   * @returns {*}
   */
  createEmbossedFilter2 () {
    let filter = new SVG.Filter()
    filter.offset(0.3333333333333333, 1.6666666666666667).in(filter.sourceAlpha).result(15)
    filter.gaussianBlur(1).in(15).result(16)
    let spec = filter.specularLighting(2, null, 15).attr({
      'specularConstant': '0.8'
    }).in(16).result(17)
    let pLight = new SVG.PointLight(-1000, -5000, 300)
    spec.add(pLight)
    filter.composite(17, 'SourceAlpha', 'in').result(18)
    filter.offset(-0.3333333333333333, -1.6666666666666667).in(filter.sourceAlpha).result(19)
    filter.gaussianBlur(1).in(19).result(20)
    let spec2 = filter.specularLighting(2, null, 6).attr({
      'specularConstant': '1.8'
    }).in(20).result(21)
    let pLight2 = new SVG.PointLight(1000, 5000, 300)
    spec2.add(pLight2)
    filter.composite(21, 'SourceAlpha', 'in').result(22)
    filter.colorMatrix(null, '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0').in(22).result(23)
    filter.composite(18, 23, 'arithmetic').attr({
      k1: 0,
      k2: 0.8,
      k3: 0.5,
      k4: 0
    }).result(24)
    filter.merge(['SourceGraphic', 24]).in(24).result(25)

    return filter
  }

  createEmbossedFilter1 (hasColor = false) {
    /* eslint-disable no-undef */
    let filter = new SVG.Filter()
    filter.gaussianBlur(2).in(filter.sourceAlpha).result('blur')
    let spec = filter.specularLighting(2, null, 15).attr({
      'specularConstant': '0.4',
      'lighting-color': 'white'
    }).result('specOut')
    let pLight = new SVG.PointLight(-5000, -5000, 8000)
    spec.add(pLight)
    filter.composite('specOut', 'SourceAlpha', 'in').result('specOut2')
    filter.composite('SourceGraphic', 'specOut2', 'arithmetic').attr({
      'k1': '0',
      'k2': '1',
      'k3': '1',
      'k4': '0'
    }).result('litPaint')

    return filter
  }

  renderProductColor () {
    this.isRendered = false

    // Clear all areas
    this.removeElements('.backgrounds')

    let manifest = _get(this.customband, 'product.manifest', {})
    let backgroundImages = []
    let productPrintStyleBgImages = _get(this.customband, 'customization.printStyle.bg_images', [])
    let colorImage = null
    let selectedIndex = _get(this.customband, 'customization.selected', 0)
    let selectedRow = _get(this.customband, `customization.rows.${selectedIndex}`, null)
    let colorContainers = _get(manifest, 'colorContainers', null)
    let group = this.draw.group().addClass('backgrounds')
    let rect = null

    // We only want backgrounds that have a path
    let manifestBgs = manifest.backgrounds.filter(img => _get(img, 'path') || _get(img, 'image'))

    // <editor-fold desc="Main Color Background">
    // Create the main color background (the color of the band)
    let color1 = _get(selectedRow, 'color1', null)
    let color1_thumbnail = _get(selectedRow, 'color1.thumbnail', null)
    if (!color1_thumbnail) {
      // Check the hex code for "dual" colors (ex. FFFFFF/00aa9e") that are used on Dual Layer
      let color1HexCode = _get(selectedRow, 'color1.hex_code', null)
      let rectColor = _get(selectedRow, 'color1.hex_code', 'FFFFFF')
      if (color1HexCode) {
        if (color1HexCode.indexOf('/') !== -1) {
          // The first color is used as the background rectangle. The second is used in the colorContainer.
          let colors = color1HexCode.split('/')
          rectColor = colors[0]
        }
      }
      // Make the rect a little smaller so we don't see any color bleed through the sides because of the miniscual resize (ex. height: 250.45px = 250 in canvas)
      rect = this.draw.defs().rect('100%', '100%').y(1).height(247).fill(`#${rectColor}`)
      // </editor-fold>
    } else {
      colorImage = {
        path: color1.thumbnail
      }
    }

    // <editor-fold desc="Color Containers">
    let blurEdges = _get(manifest, 'blurEdges', false)
    let group2 = this.draw.defs().group().addClass('colorContainers')
    let colorConts = []
    for (let i of [1, 2]) {
      let cont = _get(colorContainers, `color_${i}`, null)
      if (cont) {
        let path = this.draw.path(cont.data).addClass(`colorContainer${i}`)
        group2.add(path)

        // Get the color for the path
        let color = _get(selectedRow, `color${i + 1}`, null)
        if (this.customband.isDualLayer()) {
          color = _get(selectedRow, `color${i}`, null)
        }
        if (color.hex_code.indexOf('/') !== -1) {
          let colors = color.hex_code.split('/')
          path.fill('#' + colors[1])
        } else {
          path.fill('#' + color.hex_code)
        }
        colorConts.push(path)
      }
    }
    // </editor-fold>

    // <editor-fold desc="Background Images">
    // Here we need to copy the "image" key to "path" key since the manifest images use "path"
    Object.keys(productPrintStyleBgImages).forEach(key => {
      productPrintStyleBgImages[key].path = productPrintStyleBgImages[key].image
    })

    // If there's a color image let's add it first so any masks can overlay it
    if (colorImage) {
      backgroundImages.push(colorImage)
    }

    // Give priority to the product print style backgrounds over the manifest backgrounds
    if (!productPrintStyleBgImages.length && manifestBgs.length) {
      backgroundImages = backgroundImages.concat(backgroundImages, manifestBgs)
    } else if (productPrintStyleBgImages.length) {
      backgroundImages = backgroundImages.concat(backgroundImages, productPrintStyleBgImages)
    }

    let firstBgIsLoaded = false
    for (let bgImage of backgroundImages) {
      let imgPath = _get(bgImage, 'path', null)
      if (!imgPath) {
        // Sometimes the manifest specifies "image" instead of "path"
        imgPath = _get(bgImage, 'image', null)
      }
      let img = this.draw.image(imgPath, '100%', '100%').loaded((loader) => {
        if (!firstBgIsLoaded) {
          if (rect) {
            // Load the main color background
            let use1 = this.draw.use(rect)
            firstBgIsLoaded = true
            group.add(use1)
            use1.back()
          }

          // Load the color containers
          for (let cont of colorConts) {
            let use2 = this.draw.use(cont)

            // Blur the edges
            if (blurEdges && !this.customband.isDualLayer()) {
              use2.filter(this.edgeBlurEffect)
            }

            group.add(use2)
            use2.backward()
          }
        }
      })

      // Embed the image so we can easily save this whole SVG when it's added to the cart
      let imgBase64Key = `bb_datauri_${imgPath}`
      let imgBase64 = sessionStorage.getItem(imgBase64Key)
      if (!imgBase64) {
        axios.post('/builder/get-data-uri/', { url: imgPath }, this.axiosConfig).then((resp) => {
          if (resp.data.datauri) {
            imgBase64 = resp.data.datauri
            img.attr('href', imgBase64)
            sessionStorage.setItem(imgBase64Key, imgBase64)
          }
        })
      } else {
        img.attr('href', imgBase64)
      }

      group.add(img)
    }
    // </editor-fold>

    this.draw.defs().back()
  }

  renderAreas () {
    this.isRendered = false

    // We need to make sure all the graphics are preloaded so we don't have to wait for any asynchronous processes that are "getting" images
    // We also need to preload fonts so when the offload server grabs the image all the text elements will be resized correctly
    Promise.all([this.preloadGraphics(), this.preloadFonts()]).then(() => {
      // Clear all areas
      this.removeElements('.area')

      let areas = _get(this.customband, 'customization.areas', [])

      // Areas
      for (let area of areas) {
        this.renderArea(area)
      }

      this.isRendered = true
    })
  }

  renderArea (area) {
    let group = this.draw.group().addClass(`area`)
    let texts = _get(area, 'texts', [])

    // Texts
    for (let text of texts) {
      let textPath = text.manifest.svgPath
      let pathClassName = `pathArea${text.areaIndex}Text${text.textIndex}`

      // Clean up the old linked paths in <defs>
      this.removeElements(`.${pathClassName}`)

      let [hasColorFill, printColor] = this.getPrintColor(text)

      let tspan = null
      let _text = this.draw.text((add) => {
        tspan = add.tspan(text.text.replace(/\s/g, '\u00A0'))
      })

      let fontConfig = {
        'family': text.font,
        'size': text.manifest.textHeightMax,
        'anchor': 'middle'
      }
      _text.font(fontConfig)
      _text.attr('dominant-baseline', 'middle')
      // Some browsers (mobile & Safari on desktop) don't support "dominant-baseline" yet
      if (this.browser.is('mobile') || this.browser.is('safari') || this.browser.is('internet explorer') || this.browser.is('microsoft edge')) {
        tspan.attr('dy', '0.25em')
      }
      let _textClone = _text.clone()
      _text.path(textPath)
      let _path = _text.track()
      _text.track()
      _path.addClass(pathClassName)
      _text.textPath().attr('startOffset', '50%').fill(printColor)

      let hasGraphics = text.hasPrefixGraphic() || text.hasSuffixGraphic()
      this.resizeText(_text, _textClone, hasGraphics)

      let fontSize = _text.font('size')
      // console.log('fontSize', fontSize)

      let isLargeSize = true
      if (fontSize < 45) {
        isLargeSize = false
      }

      let printEffect = this.getPrintEffect(hasColorFill, isLargeSize)
      // console.log('printEffect', printEffect)
      if (printEffect) {
        _text.filter(printEffect)
      }

      group.add(_text)

      _textClone.remove()

      // Text Graphics
      if (text.hasPrefixGraphic()) {
        this.renderTextGraphic(text, _text, group, pathClassName, true)
      }
      if (text.hasSuffixGraphic()) {
        this.renderTextGraphic(text, _text, group, pathClassName, false)
      }
    }

    // Fixed Graphics
    let fixedGraphics = _get(area, 'graphics', [])
    for (let graphic of fixedGraphics) {
      let graphicId = `fixed${graphic.areaIndex}${graphic.graphicIndex}`

      let graphicImage = _get(graphic, 'image', null)
      if (graphicImage) {
        let label = `Fixed graphic ${graphicId}`
        // console.group(label)

        let svgPoint = graphic.manifest.svgpoint
        let svgRotation = _get(graphic, 'manifest.rotate', null)
        if (!svgRotation) {
          svgRotation = _get(graphic, 'manifest.rotation', null)
        }
        let svgWidth = _get(graphic, 'manifest.width', 50)

        let [hasColorFill, graphicFill] = this.getPrintColor(graphic)
        // console.log('hasColorFill', hasColorFill)
        // console.log('graphicFill', graphicFill)

        // console.log(`Adding fixed graphic: ${graphicId}`)
        let svgGraphic = this.addGraphic(graphic, graphicId)
        if (!svgGraphic) { continue }
        svgGraphic.addClass('graphic')
        // console.log('svgGraphic', svgGraphic)

        // Get actual width and height of SVG then scale the tallest side
        // console.log('width:', svgGraphic.width())
        // console.log('height:', svgGraphic.height())

        let graphicGroup = this.draw.group()
        graphicGroup.add(svgGraphic)
        svgGraphic.size(svgWidth)

        // console.log('width:', svgGraphic.width())
        // console.log('height:', svgGraphic.height())

        // console.log('svgPoint', svgPoint)
        this.drawDebugPoint(svgPoint, null, graphicGroup)

        svgGraphic.center(svgPoint.x, svgPoint.y)
        if (svgRotation) {
          graphicGroup.rotate(svgRotation)
        }
        svgGraphic.fill(graphicFill)

        if (!graphic.custom) {
          let printEffect = this.getPrintEffect(hasColorFill)
          if (printEffect) {
            svgGraphic.filter(printEffect)
          }
        }

        // console.log('Adding graphicGroup:', graphicGroup)
        // console.log('...to area group', group)
        group.add(graphicGroup)

        // console.log('graphicGroup.node.parentNode', graphicGroup.node.parentNode)
        // console.log('group', group.id())

        // console.groupEnd(label)
      }
    }
  }

  getPrintColor (obj) {
    let hasColorFill = false
    let objColorFillId = _get(obj, 'colorFill.id')
    let objColorFillHex = _get(obj, 'colorFill.hex_code')
    let globalColorFillHex = this.customband.getGlobalPrintColorHex()
    let hasGlobalPrintColor = !!this.customband.getGlobalPrintColorId()
    let numberOfColors = this.customband.numberOfPrintColors()

    // console.log('textColorFillId', textColorFillId)
    // console.log('textColorFillHex', objColorFillHex)
    // console.log('globalColorFillHex', globalColorFillHex)

    let printColor = '#ffffff'

    let embossedPrintColor = printColor
    if (this.customband.isEmbossed()) {
      embossedPrintColor = '#' + _get(this.customband.getSelectedRow(), 'color1.hex_code')
      printColor = embossedPrintColor
    }

    if (objColorFillId) {
      printColor = '#' + objColorFillHex
      hasColorFill = true
    }

    if (this.customband.hasMultipleItems()) {
      if (numberOfColors < 2) {
        printColor = globalColorFillHex || '#ffffff'
        hasColorFill = hasGlobalPrintColor
      }
    } else {
      // If it has all the same print colors then we check the global color fill values
      if (this.customband.hasAllTheSamePrintColors()) {
        if (globalColorFillHex) {
          printColor = globalColorFillHex
          hasColorFill = true
        } else {
          printColor = this.customband.isEmbossed() ? embossedPrintColor : printColor
        }
      }
    }

    // Check the hex code for "dual" colors (ex. FFFFFF/00aa9e") that are used on Dual Layer
    if (this.customband.isDualLayer() && !this.customband.isPrinted()) {
      let color1 = _get(this.customband.getSelectedRow(), 'color1', null)
      if (color1.hex_code.indexOf('/') !== -1) {
        // The second color is used as the colorfill
        let colors = color1.hex_code.split('/')
        printColor = '#' + colors[1]
      }
    }

    // console.log('hasColorFill', hasColorFill)
    // console.log('globalColorFillHex', globalColorFillHex)
    // console.log('printColor', printColor)
    return [hasColorFill, printColor]
  }

  renderTextGraphic (text, _text, group, pathClassName, isPrefix) {
    // console.group('renderTextGraphic')

    let charMaxHeight = this.getCharMaxHeight(_text.clone(), text.manifest.textHeightMax)
    // console.log('charMaxHeight', charMaxHeight)
    let graphic = isPrefix ? text.prefixGraphic : text.suffixGraphic

    let [hasColorFill, graphicFill] = this.getPrintColor(graphic)

    // Exception for the Marquee Oval
    if (this.customband.product.name === 'Marquee Oval') {
      charMaxHeight = Math.min(charMaxHeight, 80)
    }

    // Shrink down the graphic a little so it fits better
    let graphicHeight = charMaxHeight * 0.75

    // Import the graphic (SVG) into the main SVG
    let otherIndex = isPrefix ? 'prefix' : 'suffix'
    let graphicId = `graphic${text.areaIndex}${text.textIndex}${otherIndex}`
    let textGraphic = this.addGraphic(graphic, graphicId)
    if (!textGraphic) { return null }

    // Crop off any whitespace around the SVG
    let bbox = textGraphic.bbox()
    textGraphic.viewbox(bbox.x, bbox.y, bbox.w, bbox.h)

    // Get actual width and height of SVG then scale the tallest side
    bbox = textGraphic.bbox()

    if (bbox.w > bbox.h) {
      // console.log('Resizing width to:', graphicHeight)
      textGraphic.size(graphicHeight)
    } else {
      // console.log('Resizing height to:', graphicHeight)
      textGraphic.size(null, graphicHeight)
    }

    // We need to put the SVG in it's own group so we can rotate it (transformations aren't allowed on <svg> elements)
    let graphicGroup = this.draw.group()
    graphicGroup.add(textGraphic)
    graphicGroup.addClass(pathClassName)

    let textEndPoints = this.getTextEndpoints(_text)
    // console.log('textEndPoints', textEndPoints)
    this.drawDebugPoint(textEndPoints.first, pathClassName, group)
    this.drawDebugPoint(textEndPoints.last, pathClassName, group)

    // Get the length (on the path) at where to put the graphic
    let textEndPoint = isPrefix ? textEndPoints.first : textEndPoints.last
    // console.log('textEndPoint', textEndPoint)
    let pointOnPathToPutGraphic = this.getTextGraphicPointOnPath(textGraphic, isPrefix, textEndPoint, _text)
    // console.log('pointOnPathToPutGraphic', pointOnPathToPutGraphic)
    this.drawDebugPoint(pointOnPathToPutGraphic, pathClassName, group, '#ff8423')
    this.drawDebugBox(pointOnPathToPutGraphic, bbox, pathClassName, group)
    let graphicRotation = this.calculateRotationFromPoints(textEndPoint, pointOnPathToPutGraphic, isPrefix)
    let pointGraphicTopLeft = this.getTextGraphicPoint(pointOnPathToPutGraphic, textEndPoint, textGraphic, isPrefix)
    this.drawDebugPoint(pointGraphicTopLeft, pathClassName, group)

    // Place the graphic into position
    graphicGroup.move(pointGraphicTopLeft.x, pointGraphicTopLeft.y)

    graphicGroup.rotate(graphicRotation)
    textGraphic.fill(graphicFill)


    if (!graphic.custom) {
      let printEffect = this.getPrintEffect(hasColorFill)
      if (printEffect) {
        textGraphic.filter(printEffect)
      }
    }

    group.add(graphicGroup)

    // console.groupEnd()

    return graphicGroup
  }

  preloadGraphics () {
    let images = []
    let graphics = this.customband.getGraphics()
    let graphicsNotEmpty = graphics.filter(g => g.image)
    // console.log('graphicsNotEmpty', graphicsNotEmpty)
    for (let graphic of graphicsNotEmpty) {
      let imgToUse = graphic.custom ? RB.custom_picture : graphic.image
      let graphicSvg = sessionStorage.getItem(imgToUse)
      if (!graphicSvg) {
        let imgLoaded = new Promise((resolve) => {
          axios.post('/builder/get-svg/', { svgUrl: imgToUse }, this.axiosConfig).then(resp => {
            // console.log('resp', resp)
            sessionStorage.setItem(imgToUse, resp.data.svg)
            resolve()
          })
        })
        images.push(imgLoaded)
      }
    }

    // console.log('# of Promises', images.length)

    return Promise.all(images)
  }

  preloadFonts () {
    let fonts = []
    let usedFonts = this.customband.getUsedFonts()
    for (let usedFont of usedFonts) {
      let fontLoaded = new FontFaceObserver(usedFont).load()
      fonts.push(fontLoaded)
    }

    return Promise.all(fonts)
  }

  drawDebugPoint (point, className = null, group = null, fill = '#ff1493') {
    if (this.debug) {
      let circle = this.draw.circle(5).center(point.x, point.y).fill(fill)

      if (className) {
        circle.addClass(className)
      }

      if (group) {
        group.add(circle)
      }
    }
  }

  drawDebugBox (point, bbox, className = null, group = null, fill = '#ff1493') {
    if (this.debug) {
      let rect = this.draw.rect(bbox.w, bbox.h).fill('none').stroke({ width: 1, color: fill })
      rect.cx(point.x).cy(point.y)
      // rect.move(point.x, point.y)

      if (className) {
        rect.addClass(className)
      }

      if (group) {
        group.add(rect)
      }
    }
  }

  getTextGraphicPointOnPath (textGraphic, isPrefix, textEndPoint, _text) {
    let graphicWidth = textGraphic.width()

    // This will be the center point of the graphic on the path. (text + graphicWidth / 2)
    let lengthToPutGraphic = isPrefix ? textEndPoint.length - graphicWidth / 2 : textEndPoint.length + graphicWidth / 2

    return _text.track().pointAt(lengthToPutGraphic)
  }

  getTextGraphicPoint (graphicCenterPoint, textEndPoint, textGraphic, isPrefix) {
    let graphicWidth = textGraphic.width()
    let graphicHeight = textGraphic.height()

    let xAmt = graphicWidth / 2
    let yAmt = graphicHeight / 2

    // Determine the top left x,y coordinate for the graphic
    let paddingX = isPrefix ? -this.graphicPaddingXAmt : this.graphicPaddingXAmt
    let topLeftX = graphicCenterPoint.x - xAmt + paddingX
    let topLeftY = graphicCenterPoint.y - yAmt

    return {
      x: topLeftX,
      y: topLeftY
    }
  }

  addGraphic (graphic, graphicId) {
    let graphicImage = graphic.image

    if (graphic.custom) {
      // Assign graphic to the standard "Custom Art Here" SVG
      graphicImage = RB.custom_picture
    }

    // console.log('graphicImage', graphicImage)

    // The preloadImages() ensures this image exists
    let graphicSvg = sessionStorage.getItem(graphicImage)

    if (!graphicSvg) {
      return null
    }

    // Assign an ID to the SVG to make sure it's unique
    graphicSvg = this._replaceSVGId(graphicSvg, graphicId)

    // Check if the SVG already exists in the current SVG drawing
    let svgGraphic = SVG.get(graphicId)
    if (!svgGraphic) {
      // Import the SVG into the drawing
      this.draw.svg(graphicSvg)
      svgGraphic = SVG.get(graphicId)
    }

    // Make sure the x and y have no "px" reference
    svgGraphic.x(0)
    svgGraphic.y(0)

    return svgGraphic
  }

  _getSVGId (svg) {
    //  Note: To get groups returned with match() don't use the global flag "g"
    let rgx = /<svg[^>]*id=["\']?((?:.(?!["\']?\\s+(?:\\S+)=|[>"\']))+.)["\']?/i
    let matches = svg.match(rgx)
    // console.log('matches', matches)
    if (matches) {
      return matches[1]
    }

    return null
  }

  _replaceSVGId (svg, newId) {
    let existingId = this._getSVGId(svg)
    if (existingId) {
      svg = svg.replace(existingId, newId)
    } else {
      // console.log(`ID doesn't exist on: ${svg}`)
      svg = svg.replace('<svg', `<svg id="${newId}"`)
    }

    return svg
  }

  getCustomGraphic () {

  }

  getTextEndpoints (_text) {
    // Get the text's path element
    let textPath = _text.track()

    // Get the width of the text
    let textWidth = _text.length()

    // Since the text is centered we just need half of the text width
    let halfOfTextWidth = textWidth / 2

    // Get the length from the beginning of the path to the point where text begins
    let lengthWhereTextBegins = textPath.length() / 2 - halfOfTextWidth

    // Get the leftmost point on the path (beginning of first character)
    let firstPoint = textPath.pointAt(lengthWhereTextBegins)

    // Get the length from the end of the path to the point where text ends
    let lengthWhereTextEnds = textPath.length() - (textPath.length() / 2 - halfOfTextWidth)

    // Get the rightmost point on the path (end of the last character)
    let lastPoint = textPath.pointAt(lengthWhereTextEnds)

    firstPoint.length = lengthWhereTextBegins
    lastPoint.length = lengthWhereTextEnds

    return {
      first: firstPoint,
      last: lastPoint
    }
  }

  /**
   * Resize text
   *
   * Since the text is on a path and when the text is too long the characters disappear
   *  making the width not the actual true width of all the characters. So this is why we
   *  use a clone of the original text, but without the path.
   *
   * @param textElement
   * @param textClone
   * @param hasGraphics
   */
  resizeText (textElement, textClone, hasGraphics) {
    // console.group('Resize Text')
    let maxTextWidth = textElement.track().length()
    if (hasGraphics) {
      maxTextWidth -= (this.defaultGraphicWidth + this.graphicPaddingXAmt) * 2
    }

    // MS Edge returns "px" as part of the font size
    let currentFontSize = parseInt(textElement.font('size'))
    let newFontSize = currentFontSize
    while (textClone.length() > maxTextWidth) {
      newFontSize = --currentFontSize
      // console.log(`textLength: ${textClone.length()} > maxTextWidth: ${maxTextWidth}`)
      // console.log('currentFontSize', currentFontSize)
      // console.log('newFontSize', newFontSize)
      textClone.font('size', newFontSize)
    }

    textElement.font('size', newFontSize)

    // console.groupEnd()
  }

  getWidth (ele) {
    let dims = ele.node.getBoundingClientRect()
    return dims.width
  }

  getCharMaxHeight (textElement, fontSize) {
    textElement.font({ size: fontSize })
    textElement.tspan('A')
    let extentOfFirstChar = textElement.node.getExtentOfChar(0)
    textElement.remove()
    return extentOfFirstChar.height
  }

  removeElements (query) {
    // console.log('query', query)
    let set = SVG.select(query)
    // console.log('set', set)
    set.each(function () {
      // console.log(`Removing ${query}`, this)
      this.remove()
    })
  }

  getPrintEffect (hasColorFill, isLarge = true) {
    if (this.customband.isPrinted()) {
      return null
    } else if (this.customband.isEmbossed()) {
      return this.embossedEffect
    } else if (this.customband.isDualLayer()) {
      return isLarge ? this.debossedEffect : this.debossedEffectSmall
    } else {
      if (hasColorFill) {
        return isLarge ? this.debossedEffect : this.debossedEffectSmall
      }
      return isLarge ? this.debossedEffectNoColor : this.debossedEffectNoColorSmall
    }
  }

  calculateRotationFromPoints (point1, point2, isPrefix = true) {
    // This formula calculates the angle (in degrees) between two points
    let yDif = point1.y - point2.y
    let xDif = point1.x - point2.x

    if (!isPrefix) {
      xDif *= -1
      yDif *= -1
    }

    return Math.atan2(yDif, xDif) * 180 / Math.PI
  }
}
